import React, { useEffect, useState } from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Link,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import { getAccessToken } from "../../utils/tokenManager";
import { getAuthToken } from "../../utils/getAuthToken";
import { fetchShopifyOrders } from "../../services/shopifyApi";

/** Shopify's raw OrderReturnStatus values (e.g. "IN_PROGRESS") collide with
 * displayFulfillmentStatus's own values — label them so the return chip
 * can't be mistaken for the fulfillment chip next to it. */
const RETURN_STATUS_LABELS = {
  RETURN_REQUESTED: "Return requested",
  IN_PROGRESS: "Return in progress",
  INSPECTION_COMPLETE: "Return inspected",
  RETURNED: "Returned",
  RETURN_FAILED: "Return failed",
};

const formatOrderDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

/**
 * The loading / error / empty / order-list states, shared by the accordion below (the drawer
 * variant in ChatInfo.jsx) and ShopifyOrdersPanel (the rail's own Shopify panel) so both always
 * show orders identically. Pure presentation — no state, no fetching.
 */
const OrdersBody = ({ error, loading, orders, retry }) => (
  <>
  {error ? (
    <Box p={2}>
      <Alert severity="error" sx={{ mb: 1 }}>
        {error}
      </Alert>
      <Button size="small" onClick={retry}>
        Retry
      </Button>
    </Box>
  ) : loading ? (
    <Box display="flex" justifyContent="center" p={2}>
      <CircularProgress size={24} />
    </Box>
  ) : !orders || orders.length === 0 ? (
    <Typography color="textSecondary" textAlign="center" p={2}>
      No orders found
    </Typography>
  ) : (
    <Box display="flex" flexDirection="column" gap={2} p={1}>
      {orders.map((order) => (
        <Box
          key={order.id}
          sx={{
            p: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: (theme) => theme.palette.background.default,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Typography variant="body2" fontWeight="bold">
              {order.orderNumber}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatOrderDate(order.createdAt)}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
            {order.fulfillmentStatus && (
              <Chip
                label={order.fulfillmentStatus}
                size="small"
                sx={{ fontSize: "0.7rem", height: "20px" }}
              />
            )}
            {order.financialStatus && (
              <Chip
                label={order.financialStatus}
                size="small"
                sx={{ fontSize: "0.7rem", height: "20px" }}
              />
            )}
            {order.returnStatus && order.returnStatus !== "NO_RETURN" && (
              <Chip
                label={RETURN_STATUS_LABELS[order.returnStatus] || order.returnStatus}
                size="small"
                sx={{ fontSize: "0.7rem", height: "20px" }}
              />
            )}
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="textPrimary">
              {order.totalPrice ? `${order.totalPrice} ${order.currency || ""}` : ""}
            </Typography>
            {order.statusPageUrl && (
              <Link href={order.statusPageUrl} target="_blank" rel="noopener noreferrer" variant="caption">
                View order
              </Link>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  )}
  </>
);

/**
 * Phase 4 Inbox sidebar — "Shopify Customer" badge + Accordion order history,
 * shown only for a contact whose shopifyCustomerId has been backfilled (see
 * handleCustomerCreatedOrUpdated in shopifyOrderMessagingService.js). Mirrors
 * the "Scheduled Messages" accordion's styling/lazy-fetch-on-expand pattern
 * in ChatInfo.jsx (same file, one accordion up) rather than a Redux slice —
 * consistent with how ShopifyDetailsDialog.jsx/ConnectShopifyButton.jsx were
 * built (local state + a plain services/shopifyApi.js call).
 */
const ChatInfoShopifyOrders = ({ contactId, shopifyCustomerId }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState(null);

  if (!shopifyCustomerId) return null;

  const handleChange = async (event, isExpanded) => {
    setExpanded(isExpanded);
    if (!isExpanded || !contactId || orders !== null) return;

    setLoading(true);
    setError("");
    try {
      let token = getAccessToken();
      if (!token) token = await getAuthToken();
      if (!token) {
        setError("Not authenticated.");
        return;
      }
      const data = await fetchShopifyOrders(contactId, token);
      setOrders(data);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load Shopify orders");
    } finally {
      setLoading(false);
    }
  };

  const retry = () => {
    setOrders(null);
    handleChange(null, true);
  };

  return (
    <Box>
      <Accordion
        expanded={expanded}
        onChange={handleChange}
        sx={{
          boxShadow: "0",
          bgcolor: (theme) => theme.palette.background.paper,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="shopify-orders-content"
          id="shopify-orders-header"
          sx={{
            boxShadow: "0",
            bgcolor: (theme) => theme.palette.background.paper,
          }}
        >
          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
            <ShoppingBagIcon fontSize="small" sx={{ color: (theme) => theme.palette.success.main, flexShrink: 0 }} />
            <Typography variant="subtitle1" fontWeight="bold" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
              Shopify Orders
            </Typography>
            <Chip
              label="Shopify Customer"
              size="small"
              sx={{
                bgcolor: (theme) => theme.palette.success.main,
                color: "white",
                height: "20px",
                fontSize: "0.7rem",
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            bgcolor: (theme) => theme.palette.background.paper,
            padding: "16px !important",
            maxHeight: "300px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <OrdersBody error={error} loading={loading} orders={orders} retry={retry} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

/**
 * The rail's dedicated Shopify panel (ChatInfoRail.jsx): the Shopify icon under the clock opens
 * this on its own, instead of burying the orders inside Contact Details. Same data as the
 * accordion above (the existing GET /api/integrations/shopify/orders), but there is no
 * accordion to expand — the orders load as soon as the panel opens, and re-load if the selected
 * contact changes while it is open. Renders nothing for a contact that is not a Shopify customer.
 */
export const ShopifyOrdersPanel = ({ contactId, shopifyCustomerId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState(null);

  const load = React.useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    setError("");
    try {
      let token = getAccessToken();
      if (!token) token = await getAuthToken();
      if (!token) {
        setError("Not authenticated.");
        return;
      }
      setOrders(await fetchShopifyOrders(contactId, token));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load Shopify orders");
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    setOrders(null);
    if (shopifyCustomerId) load();
  }, [shopifyCustomerId, load]);

  if (!shopifyCustomerId) return null;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} sx={{ px: 2, pt: 2 }}>
        <ShoppingBagIcon fontSize="small" sx={{ color: (theme) => theme.palette.success.main, flexShrink: 0 }} />
        <Chip
          label="Shopify Customer"
          size="small"
          sx={{ bgcolor: (theme) => theme.palette.success.main, color: "white", height: "20px", fontSize: "0.7rem" }}
        />
      </Box>
      <Box sx={{ p: 1 }}>
        <OrdersBody error={error} loading={loading} orders={orders} retry={load} />
      </Box>
    </Box>
  );
};

export default ChatInfoShopifyOrders;
